import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  TextField,
  CssBaseline,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,Link
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { decrypt, encrypt } from '../../Security';
import { AuthContext } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

function LoginUser() {
  const { setIsAuthenticated, setAdmin, setUID, setAdminId } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
    showPassword: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (prop) => (event) => {
    setFormValues({ ...formValues, [prop]: event.target.value });
    setError(null); // Clear error when user starts typing
  };

  const handleClickShowPassword = () => {
    setFormValues({ ...formValues, showPassword: !formValues.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const apiUrl = process.env.REACT_APP_PUBLIC_SERVER_URI + '/api/v1/auth/login';
      console.log('Login attempt - API URL:', apiUrl);
      console.log('Environment:', process.env.NODE_ENV);
      
      const response = await fetch(
        apiUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: encrypt({
            email: formValues.email,
            password: formValues.password
          }),
          credentials: 'include',
        }
      );

      const data = decrypt(await response.json());

      if (response.ok) {
        if (data.user) {
          setAdmin(data.user.role === 'admin');
          setAdminId(data.user.repId);

          if (data.user.role === 'user') {
            setError('Unauthorized Action. Admin login required.');
          } else {
            setUID(data.user.userId);
            setIsAuthenticated(true);
            setSuccess(true);
            setTimeout(() => navigate('/main'), 1000);
          }
        }
      } else {
        console.error('Login failed:', data);
        setError(data.msg || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection.');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <Container 
        component="main" 
        maxWidth={false}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          px: 2
        }}
      >
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '400px',
            marginTop: '10%',
            borderRadius: 3,
            boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 56, height: 56 }}>
            <LockOutlinedIcon fontSize="large" />
          </Avatar>
          
          <Typography component="h1" variant="h4" sx={{ mt: 2, mb: 3, fontWeight: 600 }}>
            Representative Login
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
              Login successful! Redirecting...
            </Alert>
          )}
          
          <Box 
            component="form" 
            onSubmit={handleSubmit}
            noValidate
            sx={{ width: '100%' }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formValues.email}
              onChange={handleChange('email')}
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                style: { borderRadius: 12 }
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={formValues.showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formValues.password}
              onChange={handleChange('password')}
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                style: { borderRadius: 12 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {formValues.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 12,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
            
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                  
                    Forgot password? Reset on the VoteVocal app.
                 
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          © {new Date().getFullYear()} VoteVocal. All rights reserved.
        </Typography>
      </Container>
    </>
  );
}

export default LoginUser;